import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import {
    DetailScrollReveal,
    TeamMemberOverlay,
    useIslandAutoplay,
} from '~/ui-rocco/pages/detail-shared';
import type { TeamMember } from '~/ui-rocco/pages/detail-shared';
import { TeamMemberSlide } from './TeamMemberSlide';
import { useTeamMemberBooking } from '../hooks/use-team-member-booking';
import { teamMemberContact } from '../utils/team-member-contact';

/**
 * §6 — Team. The numbered section label over the professional's public team
 * members as a
 * four-column full-bleed carousel with Magic Island pagination, mirroring the
 * product page's About-the-Supplier products rail. The full team grid lives on
 * the About (/info) tab's Our Team accordion; this section is the teaser.
 * Self-hides when there are no members.
 */
export const ProfessionalTeamSection = () => {
    const { teamMembers, professionalOverview } =
        useLoaderData<typeof loader>();
    // The branch list lives on the page's initial data, not the overview's.
    const initial = useProfessionalInitialRouteData();
    const { onBookMeeting } = useTeamMemberBooking(
        teamMembers,
        'ProfessionalTeamSection',
    );

    const [openMember, setOpenMember] = useState<TeamMember | null>(null);
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    // Gate the island on the scroll-reveal so its pagination cannot show (or
    // auto-advance) while the slides are still faded out.
    const [revealed, setRevealed] = useState(false);
    // ...and on the carousel actually having somewhere to scroll: at four
    // columns a small team fits in one viewport (a single snap), where island
    // pagination would show one dot that immediately reads as ended.
    const [scrollable, setScrollable] = useState(false);
    useEffect(() => {
        if (!emblaApi) return;
        const update = () =>
            setScrollable(emblaApi.scrollSnapList().length > 1);
        update();
        emblaApi.on('reInit', update);
        return () => {
            emblaApi.off('reInit', update);
        };
    }, [emblaApi]);
    const islandRef = useIslandAutoplay<HTMLElement>(emblaApi, {
        label: 'Team members',
        enabled: scrollable && revealed,
    });

    if (!teamMembers || teamMembers.length === 0) return null;
    const openRecord = teamMembers.find(
        (member) => member.id === openMember?.id,
    );

    return (
        <section ref={islandRef} className="overflow-hidden bg-ground">
            {/* Named, not numbered, in the statement's own voice — the same
                heading Who we work with carries below it. */}
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-5 md:px-[3rem] md:pt-[1.875rem]">
                <h2 className="m-0 font-sans font-medium tracking-[-0.025em] text-black max-md:text-[2rem] max-md:leading-[1.08] md:text-[3rem] md:leading-[1.05]">
                    Who we are
                </h2>
            </div>
            {/* animate={false}: the overview page already fades the whole
                section in — a second animated reveal here left the slides at
                opacity zero under an already-visible header. The observer (and
                the island gate it drives) still fires on schedule. */}
            <DetailScrollReveal
                delay={120}
                animate={false}
                onReveal={() => setRevealed(true)}
                className="w-full max-md:pt-8 md:pt-[3rem]"
            >
                <Carousel
                    setApi={setEmblaApi}
                    ariaLabel="Team members"
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Gutter padding on the viewport keeps the first/last snaps
                        aligned to the page gutter while the slides scroll
                        full-bleed to the viewport edge (the About-the-Supplier
                        treatment). Four columns between the gutters, with the
                        grid's 24px gap carried by per-item padding. */}
                    <CarouselContent
                        className="max-md:gap-3 md:-mx-[0.375rem]"
                        viewportClassName="max-md:px-4 md:px-[3rem] qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                    >
                        {teamMembers.map((member) => (
                            <CarouselItem
                                key={member.id}
                                className="max-md:basis-[46%] md:basis-1/4 md:px-[0.375rem]"
                            >
                                <TeamMemberSlide
                                    member={member}
                                    onBookMeeting={
                                        member.bookingEnabled
                                            ? onBookMeeting
                                            : undefined
                                    }
                                    onOpen={setOpenMember}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </DetailScrollReveal>
            {openMember ? (
                <TeamMemberOverlay
                    displayName={openMember.name || professionalOverview.Title}
                    // A profile lists people, but a record can still reach here
                    // without a name of its own — then the practice stands in,
                    // and it is one phrase rather than a first and last name.
                    isPerson={Boolean(openMember.name)}
                    jobTitle={openMember.jobTitle}
                    summary={openMember.summary}
                    thumbnail={openMember.image}
                    practice={{
                        id: professionalOverview.ID,
                        name: professionalOverview.Title,
                        link: professionalOverview.Link,
                        companyLogo: professionalOverview.CompanyLogo ?? '',
                        logoBackgroundColor:
                            professionalOverview.LogoBackgroundColor ?? '',
                        // The practice's own enquiry wording and branches, as
                        // every other CTA on this profile sends them. Left off,
                        // the one message started from a team member arrived
                        // with the generic default and no branch to address.
                        customEnquiryMessage:
                            professionalOverview.CustomEnquiryMessage ??
                            undefined,
                        enquiryMessageOverrideEnabled:
                            professionalOverview.EnquiryMessageOverrideEnabled ??
                            undefined,
                        enquiryMessagePlaceholder:
                            professionalOverview.EnquiryMessagePlaceholder ??
                            undefined,
                        branchOptions: initial?.branchOptions,
                    }}
                    subject={{
                        itemType: 'professional',
                        siteTreeID: professionalOverview.ID,
                    }}
                    contact={
                        openRecord
                            ? teamMemberContact(
                                  openRecord,
                                  professionalOverview.ID,
                              )
                            : undefined
                    }
                    onBookMeeting={
                        onBookMeeting && openMember.bookingEnabled
                            ? () => onBookMeeting(openMember)
                            : undefined
                    }
                    source="ProfessionalTeamSection"
                    onClose={() => setOpenMember(null)}
                />
            ) : null}
        </section>
    );
};
